/**
 * Zamowienia z B2B - podglad zamowien, ktore sklepy zlozyly na portalu b2b.sfd.pl (tylko panel admina).
 *
 * Dane: GET /sfd-zamowienia/podsumowanie (kafelki, per sklep, statusy do filtra) + GET /sfd-zamowienia
 * (tabela; sklep/status/zakres/szukaj/strona; domyslnie ostatnie 90 dni). Sklepy do filtra z GET /locations/.
 * "Odśwież z SFD" = POST /odswiez {tryb:'biezace'} -> 202 {sync_id} i odpytywanie GET /odswiez/status/<id>
 * co 3 s do ok/blad (jak w kontroli faktur). "Pobierz całą historię…" = tryb 'pelna_historia' po potwierdzeniu.
 * Klik w wiersz tabeli rozwija pozycje (pobierane z portalu przy pierwszym rozwinieciu).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { sfdZamowieniaService } from '../services/sfdZamowieniaService';
import { locationsService } from '../services/locationsService';
import {
  Podsumowanie, PerSklep, LegendaStatusow, Filtry, Tabela,
  domyslnyZakres, formatujDateCzas, komunikatBledu,
} from '../components/sfd-zamowienia';

const NA_STRONE = 100;
export const ODPYTUJ_CO_MS = 3000;
const MAX_ODPYTAN = 600;          // 30 min
const MAX_BLEDOW_STATUSU = 5;

const btnPrimary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--primary)', background: 'var(--primary)', color: '#fff' };
const btnSecondary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text)' };

/** Pasek postepu synchronizacji (ze statusu, co 3 s). */
const Postep = ({ postep }) => {
  if (!postep) return null;
  const historia = postep.tryb === 'pelna_historia';
  return (
    <div data-testid="postep-sync" style={{ marginTop: '8px', padding: '6px 10px', borderRadius: '4px', fontSize: '11px', backgroundColor: 'var(--info-light)', color: 'var(--info)' }}>
      {historia ? 'Pobieram całą historię zamówień z SFD' : 'Odświeżam zamówienia z SFD'}
      {' · '}stron: {postep.stron ?? 0} · zamówień: {postep.pobrano ?? 0} · nowych: {postep.nowych ?? 0}
      {' · '}nagłówki: {postep.naglowki_pobrane ?? 0}{postep.naglowki_do_pobrania ? ` (zostało ${postep.naglowki_do_pobrania})` : ''}
      {postep.czas_s ? ` · ${Math.round(postep.czas_s)} s` : ''}
    </div>
  );
};

const opisWyniku = (st) => {
  const czesci = [`pobrano ${st?.pobrano ?? 0} zamówień`, `nowych ${st?.nowych ?? 0}`, `zmienionych ${st?.zmienionych ?? 0}`];
  if (st?.naglowki_pobrane) czesci.push(`nagłówków ${st.naglowki_pobrane}`);
  let tekst = `Synchronizacja z SFD zakończona: ${czesci.join(', ')}`;
  if (st?.komunikat) tekst += `. ${st.komunikat}`;
  return tekst;
};

const SfdZamowieniaPage = () => {
  const [zakres, setZakres] = useState(domyslnyZakres);
  const [sklepId, setSklepId] = useState(null);     // null = wszystkie, 0 = nieznany adres
  const [status, setStatus] = useState(null);       // null = wszystkie (kod z portalu)
  const [szukaj, setSzukaj] = useState('');
  const [szukajOdlozony, setSzukajOdlozony] = useState('');
  const [strona, setStrona] = useState(1);

  const [sklepy, setSklepy] = useState([]);
  const [podsumowanie, setPodsumowanie] = useState(null);
  const [lista, setLista] = useState({ wiersze: [], razem: 0, strona: 1 });
  const [ladowaniePods, setLadowaniePods] = useState(true);
  const [ladowanieListy, setLadowanieListy] = useState(true);
  const [blad, setBlad] = useState(null);
  const [odswiezanie, setOdswiezanie] = useState(false);
  const [syncId, setSyncId] = useState(null);
  const [postep, setPostep] = useState(null);
  const [wynik, setWynik] = useState(null);         // {status: 'ok'|'blad', komunikat}
  const listaReq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setSzukajOdlozony(szukaj.trim()), 300);
    return () => clearTimeout(t);
  }, [szukaj]);

  // Sklepy do filtra (lista lokalizacji; odpowiedz moze byc tablica albo {data: [...]})
  useEffect(() => {
    let aktywny = true;
    locationsService.getLocations()
      .then((d) => {
        if (!aktywny) return;
        const tab = Array.isArray(d) ? d : (Array.isArray(d?.data) ? d.data : []);
        setSklepy(tab.map(l => ({ id: l.id, nazwa: l.nazwa || l.name || `Sklep ${l.id}` })));
      })
      .catch(() => { /* filtr sklepu bez listy - tabela i tak dziala */ });
    return () => { aktywny = false; };
  }, []);

  const ladujPodsumowanie = useCallback(async () => {
    setLadowaniePods(true);
    try {
      const dane = await sfdZamowieniaService.podsumowanie({ od: zakres.od, do: zakres.do });
      setPodsumowanie(dane || null);
      setBlad(null);
      if (dane?.sync_w_toku_id) setSyncId(prev => prev || dane.sync_w_toku_id);
    } catch (err) {
      if (!err?.isAuthError) setBlad(komunikatBledu(err, 'Nie udało się pobrać podsumowania'));
    } finally {
      setLadowaniePods(false);
    }
  }, [zakres.od, zakres.do]);

  const ladujListe = useCallback(async () => {
    const nr = ++listaReq.current;
    setLadowanieListy(true);
    try {
      const params = { od: zakres.od, do: zakres.do, szukaj: szukajOdlozony, page: strona, limit: NA_STRONE };
      if (sklepId !== null && sklepId !== undefined) params.sklep_id = sklepId;
      if (status !== null && status !== undefined) params.status = status;
      const dane = await sfdZamowieniaService.lista(params);
      if (nr !== listaReq.current) return;
      setLista({ wiersze: Array.isArray(dane?.wiersze) ? dane.wiersze : [], razem: dane?.razem ?? 0, strona: dane?.strona ?? strona });
    } catch (err) {
      if (nr !== listaReq.current) return;
      if (!err?.isAuthError) setBlad(komunikatBledu(err, 'Nie udało się pobrać listy zamówień'));
    } finally {
      if (nr === listaReq.current) setLadowanieListy(false);
    }
  }, [zakres.od, zakres.do, szukajOdlozony, strona, sklepId, status]);

  useEffect(() => { ladujPodsumowanie(); }, [ladujPodsumowanie]);
  useEffect(() => { ladujListe(); }, [ladujListe]);

  const przeladuj = useCallback(() => { ladujPodsumowanie(); ladujListe(); }, [ladujPodsumowanie, ladujListe]);
  const zmienSklep = (id) => { setSklepId(id); setStrona(1); };
  const zmienStatus = (kod) => { setStatus(kod); setStrona(1); };
  const zmienSzukaj = (v) => { setSzukaj(v); setStrona(1); };
  const zmienZakres = (z) => { setZakres(z); setStrona(1); };

  const uruchomSync = async (tryb) => {
    setOdswiezanie(true);
    setWynik(null);
    try {
      const odp = await sfdZamowieniaService.odswiez(tryb);
      if (odp?.sync_id) {
        setPostep({ tryb, stron: 0, pobrano: 0, nowych: 0 });
        setSyncId(odp.sync_id);
      } else {
        setWynik({ status: 'blad', komunikat: 'Backend nie zwrócił identyfikatora synchronizacji' });
      }
    } catch (err) {
      if (!err?.isAuthError) {
        const komunikat = komunikatBledu(err, 'Nie udało się uruchomić synchronizacji z SFD.');
        setWynik({ status: 'blad', komunikat });
        toast.error(komunikat);
      }
    } finally {
      setOdswiezanie(false);
    }
  };

  const odswiez = () => uruchomSync('biezace');
  const pobierzHistorie = () => {
    const ok = window.confirm('Pobrać całą historię zamówień z portalu SFD (od 2022, okna po 90 dni)? To może potrwać kilkanaście minut — działa w tle.');
    if (ok) uruchomSync('pelna_historia');
  };

  // Odpytywanie statusu synchronizacji co 3 s (stan w bazie - dziala po odswiezeniu strony i miedzy workerami)
  useEffect(() => {
    if (!syncId) return undefined;
    let aktywny = true;
    let timer = null;
    let bledy = 0;
    let odpytan = 0;
    const zakoncz = () => { aktywny = false; setSyncId(null); setPostep(null); };
    const tick = async () => {
      if (!aktywny) return;
      odpytan += 1;
      try {
        const st = await sfdZamowieniaService.statusOdswiezania(syncId);
        if (!aktywny) return;
        if (st?.status === 'ok') {
          zakoncz();
          const komunikat = opisWyniku(st);
          setWynik({ status: 'ok', komunikat });
          toast.success(komunikat);
          await Promise.all([ladujPodsumowanie(), ladujListe()]);
          return;
        }
        if (st?.status === 'blad') {
          zakoncz();
          const komunikat = st.komunikat || 'Synchronizacja zakończyła się błędem';
          setWynik({ status: 'blad', komunikat });
          toast.error(komunikat);
          await Promise.all([ladujPodsumowanie(), ladujListe()]);
          return;
        }
        bledy = 0;
        setPostep(prev => ({ ...(prev || {}), ...(st || {}), status: 'w_toku' }));
      } catch (err) {
        if (!aktywny) return;
        bledy += 1;
        if (err?.isAuthError || bledy >= MAX_BLEDOW_STATUSU) {
          zakoncz();
          setWynik({ status: 'blad', komunikat: `Nie udało się sprawdzić stanu synchronizacji (${err?.message || 'błąd'}). Odśwież stronę za chwilę.` });
          return;
        }
      }
      if (odpytan >= MAX_ODPYTAN) {
        zakoncz();
        setWynik({ status: 'blad', komunikat: 'Synchronizacja trwa zbyt długo — sprawdź ponownie za chwilę (Przeładuj).' });
        return;
      }
      if (aktywny) timer = setTimeout(tick, ODPYTUJ_CO_MS);
    };
    timer = setTimeout(tick, ODPYTUJ_CO_MS);
    return () => { aktywny = false; if (timer) clearTimeout(timer); };
  }, [syncId, ladujPodsumowanie, ladujListe]);

  const razemStron = Math.max(1, Math.ceil((lista.razem || 0) / NA_STRONE));
  const zajety = odswiezanie || Boolean(syncId);
  const brakKonfiguracji = Boolean(podsumowanie) && podsumowanie.skonfigurowane === false;
  const ostatnia = podsumowanie?.ostatnia_synchronizacja;

  return (
    <div style={{ fontSize: '12px' }}>
      <div className="card" style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Zamówienia z B2B</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
              Zamówienia złożone przez sklepy na portalu b2b.sfd.pl — tylko podgląd (nic nie zapisuje w magazynie)
              {' · '}
              <span data-testid="ostatnia-sync">
                ostatnie odświeżenie: {ostatnia?.zakonczono ? formatujDateCzas(ostatnia.zakonczono) : (ostatnia?.status === 'w_toku' ? 'trwa…' : 'nigdy')}
              </span>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={przeladuj} disabled={odswiezanie} style={btnSecondary}>Przeładuj</button>
            <button type="button" onClick={odswiez} disabled={zajety || brakKonfiguracji} data-testid="btn-odswiez"
              title={brakKonfiguracji ? 'Najpierw uzupełnij e-mail i hasło do portalu w zakładce SFD B2B' : 'Pobierze z portalu SFD zamówienia z ostatnich 90 dni i nagłówki (sklep, uwagi)'}
              style={{ ...btnPrimary, opacity: zajety || brakKonfiguracji ? 0.6 : 1 }}>
              {zajety ? 'Pobieram z SFD…' : 'Odśwież z SFD'}
            </button>
            <button type="button" onClick={pobierzHistorie} disabled={zajety || brakKonfiguracji} data-testid="btn-historia"
              title="Pobierze wszystkie zamówienia z portalu (okna po 90 dni wstecz) — potwierdzenie"
              style={{ ...btnSecondary, border: '1px solid var(--primary)', color: 'var(--primary)', opacity: zajety || brakKonfiguracji ? 0.6 : 1 }}>
              Pobierz całą historię…
            </button>
          </div>
        </div>
        {brakKonfiguracji && (
          <div data-testid="brak-konfiguracji" style={{ marginTop: '8px', padding: '6px 10px', borderRadius: '4px', fontSize: '11px', backgroundColor: 'var(--warning-light)', color: 'var(--warning)' }}>
            Brak e-maila lub hasła do portalu SFD — uzupełnij w zakładce „SFD B2B”, potem kliknij „Odśwież z SFD”.
          </div>
        )}
        <Postep postep={postep} />
        {wynik && (
          <div data-testid="wynik-odswiezania" style={{
            marginTop: '8px', padding: '6px 10px', borderRadius: '4px', fontSize: '11px',
            backgroundColor: wynik.status === 'ok' ? 'var(--success-light)' : 'var(--danger-light)',
            color: wynik.status === 'ok' ? 'var(--success)' : 'var(--danger)',
          }}>
            {wynik.komunikat}
          </div>
        )}
      </div>

      {blad && (
        <div data-testid="blad" style={{ padding: '8px 12px', backgroundColor: 'var(--danger-light)', color: 'var(--danger)', borderRadius: '4px', marginBottom: '12px', fontSize: '12px' }}>
          {'❌'} {blad}
        </div>
      )}

      <div className="card" style={{ marginBottom: '12px' }}>
        {ladowaniePods && !podsumowanie
          ? <div style={{ color: 'var(--text-muted)', padding: '4px 0' }}>Ładowanie podsumowania…</div>
          : <Podsumowanie podsumowanie={podsumowanie} />}
        <div style={{ marginTop: '8px' }}><LegendaStatusow legenda={podsumowanie?.legenda} /></div>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <PerSklep perSklep={podsumowanie?.per_sklep} aktywnySklepId={sklepId} onWybierz={zmienSklep} />
      </div>

      <div className="card" style={{ marginBottom: '12px' }}>
        <div style={{ marginBottom: '10px' }}>
          <Filtry
            sklepy={sklepy} sklepId={sklepId} onSklep={zmienSklep}
            statusy={podsumowanie?.statusy} status={status} onStatus={zmienStatus}
            szukaj={szukaj} onSzukaj={zmienSzukaj} zakres={zakres} onZakres={zmienZakres}
          />
        </div>
        <Tabela wiersze={lista.wiersze} ladowanie={ladowanieListy} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>
          <span data-testid="licznik-wierszy">Razem: {lista.razem} · strona {lista.strona} z {razemStron}</span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button type="button" disabled={strona <= 1 || ladowanieListy} onClick={() => setStrona(s => Math.max(1, s - 1))} style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px' }}>‹ Poprzednia</button>
            <button type="button" disabled={strona >= razemStron || ladowanieListy} onClick={() => setStrona(s => s + 1)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px' }}>Następna ›</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SfdZamowieniaPage;
